/**
 * Rotten Charm — Header interactions
 * No dependencies. One instance per <header data-rc-header> on the page.
 */
(function () {
  'use strict';

  const SCROLL_THRESHOLD = 40; // px before header is considered "scrolled"

  function trapFocus(container, closeFn) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeFn();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return () => container.removeEventListener('keydown', onKeydown);
  }

  class RottenHeader {
    constructor(el) {
      this.el = el;
      this.stickyMode = el.dataset.stickyMode;
      this.lastScrollY = window.scrollY;
      this.releaseFocusTrap = null;

      this.menuToggle = el.querySelector('[data-rc-menu-toggle]');
      this.menuClose = document.querySelectorAll('[data-rc-menu-close]');
      this.mobileMenu = document.getElementById(el.querySelector('[data-rc-menu-toggle]')?.getAttribute('aria-controls'));

      this.searchToggle = el.querySelector('[data-rc-search-toggle]');
      this.searchClose = el.querySelector('[data-rc-search-close]');
      this.searchPanel = el.querySelector('[data-rc-search-panel]');
      this.searchInput = el.querySelector('[data-rc-search-input]');

      this.cartOpenBtn = el.querySelector('[data-rc-cart-open]');

      this.bindScroll();
      this.bindMenu();
      this.bindSearch();
      this.bindSubmenus();
      this.bindCartOpen();
      this.bindCartCountUpdates();
    }

    bindScroll() {
      if (this.stickyMode === 'none') return;

      let ticking = false;
      const update = () => {
        const y = window.scrollY;
        this.el.dataset.scrolled = y > SCROLL_THRESHOLD ? 'true' : 'false';

        if (this.stickyMode === 'smart') {
          const scrollingDown = y > this.lastScrollY;
          const pastThreshold = y > this.el.offsetHeight;
          this.el.dataset.hidden = scrollingDown && pastThreshold ? 'true' : 'false';
        }

        this.lastScrollY = y;
        ticking = false;
      };

      window.addEventListener(
        'scroll',
        () => {
          if (!ticking) {
            requestAnimationFrame(update);
            ticking = true;
          }
        },
        { passive: true }
      );
    }

    bindMenu() {
      if (!this.menuToggle || !this.mobileMenu) return;

      const open = () => {
        this.mobileMenu.hidden = false;
        requestAnimationFrame(() => {
          this.mobileMenu.dataset.open = 'true';
        });
        this.menuToggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.releaseFocusTrap = trapFocus(this.mobileMenu, close);
      };

      const close = () => {
        this.mobileMenu.dataset.open = 'false';
        this.menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        if (this.releaseFocusTrap) this.releaseFocusTrap();
        setTimeout(() => {
          this.mobileMenu.hidden = true;
        }, 320);
        this.menuToggle.focus();
      };

      this.menuToggle.addEventListener('click', open);
      this.menuClose.forEach((btn) => btn.addEventListener('click', close));

      // Nested submenu toggles inside mobile menu
      this.mobileMenu.querySelectorAll('[data-rc-mobile-submenu-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const submenu = btn.nextElementSibling;
          const expanded = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!expanded));
          if (submenu) submenu.hidden = expanded;
        });
      });
    }

    bindSearch() {
      if (!this.searchToggle || !this.searchPanel) return;

      const open = () => {
        this.searchPanel.hidden = false;
        this.searchToggle.setAttribute('aria-expanded', 'true');
        if (this.searchInput) this.searchInput.focus();
      };

      const close = () => {
        this.searchPanel.hidden = true;
        this.searchToggle.setAttribute('aria-expanded', 'false');
      };

      this.searchToggle.addEventListener('click', () => {
        const isOpen = this.searchToggle.getAttribute('aria-expanded') === 'true';
        isOpen ? close() : open();
      });

      if (this.searchClose) this.searchClose.addEventListener('click', close);
    }

    bindSubmenus() {
      this.el.querySelectorAll('[data-rc-submenu-toggle]').forEach((btn) => {
        const submenu = document.getElementById(btn.getAttribute('aria-controls'));
        if (!submenu) return;

        const open = () => {
          btn.setAttribute('aria-expanded', 'true');
          submenu.dataset.open = 'true';
        };
        const close = () => {
          btn.setAttribute('aria-expanded', 'false');
          submenu.dataset.open = 'false';
        };

        btn.addEventListener('click', () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          isOpen ? close() : open();
        });

        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            close();
            btn.focus();
          }
        });

        document.addEventListener('click', (e) => {
          if (!btn.parentElement.contains(e.target)) close();
        });
      });
    }

    bindCartOpen() {
      if (!this.cartOpenBtn) return;
      this.cartOpenBtn.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
    }

    bindCartCountUpdates() {
      // cart-drawer.js dispatches this after every successful cart mutation
      document.addEventListener('cart:updated', (e) => {
        const countEl = this.el.querySelector('[data-rc-cart-count]');
        if (!countEl || !e.detail) return;
        const count = e.detail.item_count;
        countEl.textContent = count;
        countEl.classList.toggle('rc-header__cart-count--hidden', count === 0);
      });
    }
  }

  document.querySelectorAll('[data-rc-header]').forEach((el) => new RottenHeader(el));
})();
